// Three.js materials built on the procedural textures: map surfaces, weapons, team-coloured bodies.
// Textures come from the shared cache in textures.ts; materials created here own only themselves.

import * as THREE from "three";
import { ARCH, ACCENT, GUN } from "./palette";
import { applyCharacterShader } from "./characterShader";
import type { CharacterUniforms } from "./characterShader";
import { getCamoTexture, getSurfaceTexture } from "./textures";
import { CAMO_NAMES } from "./types";
import type { CamoName, MaterialDef, TextureKind } from "./types";
import type { BakedLight } from "../bakedLight";

/**
 * Material for a map surface. Plain, concrete and tiles use one neutral texture per kind and take
 * their colour from the material (`tint` x `gain`); the other kinds have the colour painted in
 * and the tint is white. Geometry UVs carry the uv scale (`def.uvScale` is not applied here).
 * With `baked` the surface takes the map's baked light (occlusion, bounce, lamps); without it the
 * material is plain hemisphere + sun lighting.
 */
export function createSurfaceMaterial(
  def: MaterialDef,
  baked: BakedLight | null = null,
): THREE.MeshStandardMaterial {
  const { map, tint, gain } = getSurfaceTexture(
    def.texture,
    def.color,
    def.grime,
  );
  const mat = new THREE.MeshStandardMaterial({
    map,
    color: tint,
    roughness: def.roughness,
    metalness: def.metalness,
  });
  mat.color.multiplyScalar(gain * (1 - 0.15 * def.grime));
  mat.name = `surf:${def.texture}`;

  if (def.emissive !== undefined) {
    mat.emissive.set(def.emissive);
    mat.emissiveIntensity = def.emissiveIntensity ?? 1;
  } else if (def.emissiveIntensity !== undefined) {
    mat.emissiveIntensity = def.emissiveIntensity;
  }

  if (
    (def.opacity !== undefined && def.opacity < 1) ||
    def.texture === "glass"
  ) {
    mat.transparent = true;
    mat.opacity = def.opacity ?? 0.4;
    mat.depthWrite = false;
  }
  if (def.texture === "grate") {
    // Crisp alpha holes are cut out, not blended.
    mat.alphaTest = 0.5;
    mat.side = THREE.DoubleSide;
  }
  baked?.patch(mat, def.grime);
  return mat;
}

/** One tasteful sample per texture kind; the model viewer builds its texture swatch wall from these. */
export const SAMPLE_MATERIALS: Record<TextureKind, MaterialDef> = {
  plain: {
    color: ARCH.offWhite,
    texture: "plain",
    uvScale: 4,
    roughness: 0.85,
    metalness: 0,
    grime: 0,
  },
  concrete: {
    color: ARCH.concrete,
    texture: "concrete",
    uvScale: 4,
    roughness: 0.9,
    metalness: 0,
    grime: 0.4,
  },
  tiles: {
    color: ARCH.offWhite,
    texture: "tiles",
    uvScale: 2,
    roughness: 0.35,
    metalness: 0,
    grime: 0.25,
  },
  panel: {
    color: ARCH.midGrey,
    texture: "panel",
    uvScale: 2,
    roughness: 0.6,
    metalness: 0.2,
    grime: 0.25,
  },
  metal: {
    color: ARCH.midGrey,
    texture: "metal",
    uvScale: 1,
    roughness: 0.4,
    metalness: 0.8,
    grime: 0.25,
  },
  grate: {
    color: ARCH.darkTrim,
    texture: "grate",
    uvScale: 2,
    roughness: 0.5,
    metalness: 0.6,
    grime: 0.25,
  },
  stripes: {
    color: ACCENT.safetyYellow,
    texture: "stripes",
    uvScale: 2,
    roughness: 0.75,
    metalness: 0,
    grime: 0.25,
  },
  glass: {
    color: "#9fd8e6",
    texture: "glass",
    uvScale: 2,
    roughness: 0.05,
    metalness: 0,
    grime: 0,
    opacity: 0.35,
  },
  wood: {
    color: "#a8794a",
    texture: "wood",
    uvScale: 2,
    roughness: 0.75,
    metalness: 0,
    grime: 0.25,
  },
  asphalt: {
    color: "#3a3b3e",
    texture: "asphalt",
    uvScale: 4,
    roughness: 0.95,
    metalness: 0,
    grime: 0.5,
  },
};

export const WEAPON_MATERIAL_KEYS = [
  "body",
  "trim",
  "metal",
  "accent",
  "lens",
  "emit",
] as const;
export type WeaponMaterialKey = (typeof WEAPON_MATERIAL_KEYS)[number];

export interface WeaponMaterials {
  readonly mats: Record<WeaponMaterialKey, THREE.Material>;
  /** Meters per texture repeat for the body material; the geometry builder bakes it into body UVs. */
  readonly bodyUvMeters: number;
  /** Disposes the materials only; the shared textures stay in the cache. */
  dispose(): void;
}

/** How the weapon body looks per camo. `color` is used when the camo has no texture. */
interface BodySpec {
  readonly color: string;
  readonly roughness: number;
  readonly metalness: number;
  readonly uvMeters: number;
  /** Texture doubles as emissive map at this intensity. */
  readonly emissiveIntensity?: number;
}

const BODY_SPECS: Record<CamoName, BodySpec> = {
  none: { color: GUN.polymer, roughness: 0.85, metalness: 0.05, uvMeters: 1 },
  carbon: {
    color: "#ffffff",
    roughness: 0.35,
    metalness: 0.35,
    uvMeters: 0.12,
  },
  hazard: { color: "#ffffff", roughness: 0.7, metalness: 0.1, uvMeters: 0.3 },
  arctic: { color: "#ffffff", roughness: 0.8, metalness: 0.05, uvMeters: 0.6 },
  ember: {
    color: "#ffffff",
    roughness: 0.55,
    metalness: 0.2,
    uvMeters: 0.5,
    emissiveIntensity: 0.35,
  },
  prism: { color: "#ffffff", roughness: 0.28, metalness: 0.7, uvMeters: 0.5 },
  gold: { color: "#d9a920", roughness: 0.28, metalness: 1, uvMeters: 1 },
};

/**
 * Fresh material instances for one weapon (callers may change their opacity). An unknown camo
 * string behaves like "none".
 */
export function createWeaponMaterials(camo: string): WeaponMaterials {
  const camoName: CamoName = (CAMO_NAMES as readonly string[]).includes(camo)
    ? (camo as CamoName)
    : "none";
  const spec = BODY_SPECS[camoName];
  const camoTex = getCamoTexture(camoName);
  const gold = camoName === "gold";

  const body = new THREE.MeshStandardMaterial({
    color: spec.color,
    map: camoTex,
    roughness: spec.roughness,
    metalness: spec.metalness,
  });
  if (camoTex && spec.emissiveIntensity !== undefined) {
    body.emissive.set("#ffffff");
    body.emissiveMap = camoTex;
    body.emissiveIntensity = spec.emissiveIntensity;
  }

  const trim = new THREE.MeshStandardMaterial({
    color: gold ? "#3a3326" : "#2b2d31",
    roughness: 0.9,
    metalness: 0.05,
  });

  // Worn steel; the geometry builder maps it at 0.3 m per repeat.
  const metal = gold
    ? new THREE.MeshStandardMaterial({
        color: "#b8861c",
        roughness: 0.35,
        metalness: 1,
      })
    : new THREE.MeshStandardMaterial({
        map: getSurfaceTexture("metal", GUN.steel, 0.5).map,
        roughness: 0.45,
        metalness: 0.85,
      });

  const accent = new THREE.MeshStandardMaterial({
    color: GUN.accent,
    roughness: 0.6,
    metalness: 0.1,
  });

  const lens = new THREE.MeshStandardMaterial({
    color: GUN.lens,
    transparent: true,
    opacity: 0.4,
    roughness: 0.05,
    metalness: 0,
    emissive: GUN.lens,
    emissiveIntensity: 0.15,
    depthWrite: false,
  });

  // Reticle / laser dot: unlit and outside tone mapping so bloom picks it up.
  const emit = new THREE.MeshBasicMaterial({ color: GUN.redDot });
  emit.toneMapped = false;

  const mats: Record<WeaponMaterialKey, THREE.Material> = {
    body,
    trim,
    metal,
    accent,
    lens,
    emit,
  };
  for (const key of WEAPON_MATERIAL_KEYS) mats[key].name = `weapon:${key}`;

  return {
    mats,
    bodyUvMeters: spec.uvMeters,
    dispose() {
      for (const key of WEAPON_MATERIAL_KEYS) mats[key].dispose();
    },
  };
}

export interface TeamMaterial {
  material: THREE.MeshStandardMaterial;
  /** The material's own inputs (spawn-protection shimmer). */
  uniforms: CharacterUniforms;
}

/**
 * Material of a team-coloured player model: vertex colours and the `aPart` attribute select the
 * surface, `primary` (the team colour, hex) tints the rim light. All of them share one shader
 * program. Transparent from the start: opacity is a uniform, but `transparent` is compiled into the
 * shader, and flipping it at the first death would recompile.
 */
export function createTeamMaterial(primary: string): TeamMaterial {
  const material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.82,
    metalness: 0.05,
    transparent: true,
  });
  const uniforms = applyCharacterShader(material, primary);
  return { material, uniforms };
}
